import { apiClient, getAuthHeaders, ApiResponse } from './api';

// Customer Profile interfaces
export interface CustomerProfile {
  id: string;
  company_name: string;
  contact_person: string | null;
  phone: string | null;
  address: string | null;
  epicor_customer_id: string | null;
  credit_limit: number;
  outstanding_balance: number;
  available_credit: number;
  credit_hold: boolean;
  customer_group: string | null;
  price_list_code: string | null;
  is_approved: boolean;
  last_sync_at: string | null;
  created_at: string;
}

export interface CustomerSummary {
  id: string;
  company_name: string;
  epicor_customer_id: string | null;
  credit_limit: number;
  outstanding_balance: number;
  is_approved: boolean;
}

export interface CustomerUpdateRequest {
  company_name?: string;
  contact_person?: string;
  phone?: string;
  address?: string;
}

export interface DashboardStats {
  recent_orders: number;
  pending_invoices: number;
  account_balance: number;
}

export interface SalesRep {
  code: string;
  name: string | null;
  email: string | null;
  role_code: string | null;
  role_description: string | null;
  is_active: boolean;
}

export interface SalesRepResponse {
  sales_rep: SalesRep | null;
  sales_rep_code?: string | null;
}

class CustomerService {
  /**
   * Get dashboard statistics for the current customer
   */
  async getDashboardStats(): Promise<DashboardStats> {
    return apiClient.get<DashboardStats>('/customers/dashboard/stats', getAuthHeaders());
  }

  /**
   * Get the current user's customer profile
   */
  async getProfile(): Promise<CustomerProfile> {
    return apiClient.get<CustomerProfile>('/customers/profile', getAuthHeaders());
  }

  /**
   * Get the current user's assigned sales rep (name + email + role),
   * resolved from the local workforce cache.
   */
  async getMySalesRep(): Promise<SalesRepResponse> {
    return apiClient.get<SalesRepResponse>('/customers/profile/sales-rep', getAuthHeaders());
  }

  /**
   * Get customer profile by Epicor customer ID (admin/staff access)
   */
  async getProfileByEpicorId(epicorCustomerId: string): Promise<CustomerProfile> {
    return apiClient.get<CustomerProfile>(
      `/customers/profile/${epicorCustomerId}`,
      getAuthHeaders()
    );
  }

  /**
   * List all customers (admin/staff access)
   */
  async listCustomers(
    skip: number = 0,
    limit: number = 100,
    approvedOnly?: boolean
  ): Promise<CustomerSummary[]> {
    let endpoint = `/customers/?skip=${skip}&limit=${limit}`;
    if (approvedOnly !== undefined) {
      endpoint += `&approved_only=${approvedOnly}`;
    }
    return apiClient.get<CustomerSummary[]>(endpoint, getAuthHeaders());
  }

  /**
   * Update customer profile
   */
  async updateProfile(updates: CustomerUpdateRequest): Promise<CustomerProfile> {
    return apiClient.put<CustomerProfile>(
      '/customers/profile',
      updates,
      getAuthHeaders()
    );
  }

  /**
   * Format currency amount for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Calculate credit utilization percentage
   */
  calculateCreditUtilization(outstandingBalance: number, creditLimit: number): number {
    if (creditLimit === 0) return 0;
    return (outstandingBalance / creditLimit) * 100;
  }

  /**
   * Get credit status color based on utilization
   */
  getCreditStatusColor(utilizationPercentage: number): string {
    if (utilizationPercentage >= 90) return '#f44336'; // Red - Critical
    if (utilizationPercentage >= 70) return '#ff9800'; // Orange - Warning
    if (utilizationPercentage >= 50) return '#2196f3'; // Blue - Moderate
    return '#4caf50'; // Green - Good
  }

  /**
   * Get credit status text
   */
  getCreditStatusText(utilizationPercentage: number): string {
    if (utilizationPercentage >= 90) return 'Critical';
    if (utilizationPercentage >= 70) return 'Warning';
    if (utilizationPercentage >= 50) return 'Moderate';
    return 'Good';
  }
}

export const customerService = new CustomerService();